/**
 * sl-apue: SeatAddon Entity — Reactive state for workspace seat addons
 */
import type {
  MemberQuotaInfoQuery,
  WorkspaceSeatAddonQuery,
} from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { SeatAddonStore } from '../stores/seat-addon';

export type SeatAddonInfo = NonNullable<
  WorkspaceSeatAddonQuery['workspaceSeatAddon']
>;

export type MemberQuotaInfo = MemberQuotaInfoQuery['memberQuotaInfo'];

export class SeatAddon extends Entity {
  addon$ = new LiveData<SeatAddonInfo | null>(null);
  memberQuota$ = new LiveData<MemberQuotaInfo | null>(null);
  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any | null>(null);

  /** Whether this workspace has active extra seats */
  hasExtraSeats$ = this.addon$.map(
    addon => !!addon && addon.status === 'active' && addon.quantity > 0
  );

  /** Whether extra seats can be purchased for this workspace */
  canPurchaseSeats$ = this.memberQuota$.map(
    quota => quota?.seatTiers != null && quota.maxExtraSeats !== 0
  );

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly store: SeatAddonStore
  ) {
    super();
  }

  async purchaseSeats(quantity: number, withdrawalConsent = true) {
    const workspaceId = this.workspaceService.workspace.id;
    const result = await this.store.mutatePurchaseExtraSeats(
      workspaceId,
      quantity,
      withdrawalConsent
    );
    await this.waitForRevalidation();
    return result;
  }

  async updateSeatCount(quantity: number, withdrawalConsent = true) {
    const workspaceId = this.workspaceService.workspace.id;
    const result = await this.store.mutateUpdateSeatCount(
      workspaceId,
      quantity,
      withdrawalConsent
    );
    await this.waitForRevalidation();
    return result;
  }

  async removeSeats() {
    const workspaceId = this.workspaceService.workspace.id;
    await this.store.mutateRemoveSeatAddon(workspaceId);
    await this.waitForRevalidation();
  }

  async waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler which defers the inner
    // observable subscription to the next macrotask. Without this, waitFor
    // sees the current false value and resolves immediately — before the
    // revalidation fetch even starts.
    this.isRevalidating$.next(true);
    this.revalidate();
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  revalidate = effect(
    exhaustMapWithTrailing(() => {
      return fromPromise(async signal => {
        const workspaceId = this.workspaceService.workspace.id;
        if (!workspaceId) return null;

        const [addon, memberQuota] = await Promise.all([
          this.store.fetchSeatAddon(workspaceId, signal),
          this.store.fetchMemberQuotaInfo(workspaceId, signal),
        ]);

        return { addon, memberQuota };
      }).pipe(
        smartRetry(),
        tap(data => {
          if (data) {
            this.addon$.next(data.addon ?? null);
            this.memberQuota$.next(data.memberQuota);
          }
        }),
        catchErrorInto(this.error$),
        onStart(() => this.isRevalidating$.next(true)),
        onComplete(() => this.isRevalidating$.next(false))
      );
    })
  );

  reset() {
    this.addon$.next(null);
    this.memberQuota$.next(null);
    this.isRevalidating$.next(false);
    this.error$.next(null);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
